import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/store/store";
import { isFetchResult, reusableFetch } from "@/utils/useFetch";

export interface Theme {
  primary: Record<string, string>;
  neutral: Record<string, string>;
  danger: Record<string, string>;
  success: Record<string, string>;
  warning: Record<string, string>;
  tenancyLogo: string;
}

interface ThemeSlice {
  loading: boolean;
  theme: Theme | null;
  authLogo: string | null;
}

export type { ThemeSlice };

interface ThemeApiResponse {
  [key: string]: unknown;
  data: {
    theme: Theme;
    authLogo: string;
  };
}

export const getTheme = createAsyncThunk<ThemeApiResponse, void, { rejectValue: unknown }>(
  "theme/getTheme",
  async (_, { rejectWithValue }) => {
    const response = await reusableFetch("theme", "GET", null, rejectWithValue);
    if (!isFetchResult(response)) return response as ThemeApiResponse;
    return response.data as ThemeApiResponse;
  },
);

const initialState: ThemeSlice = {
  loading: false,
  theme: null,
  authLogo: null,
};

export const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getTheme.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTheme.fulfilled, (state, { payload }) => {
        const { theme, authLogo } = payload.data;
        state.loading = false;
        state.theme = theme;
        state.authLogo = authLogo;
      })
      .addCase(getTheme.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const themeSelector = (state: RootState) => {
  return state.theme;
};

export default themeSlice.reducer;
